import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useWizard } from '../../hooks/WizardProvider';
import StepPanel from '../../components/StepPanel';
import WizardFooter from '../../components/WizardFooter';
import Field from '../../components/ui/Field';
import { WIZARD_CAMPAIGN_GOALS, GOAL_LABEL_BY_VALUE } from '../../utils/campaignGoals';
import { useCampaignTemplates } from '../../hooks/useCampaignTemplates';

const BRAND_OPTIONS = [
  { value: 'single_brand', label: 'Single Brand' },
  { value: 'multi_brand', label: 'Multi Brand' },
];

const SPACE_CODE_OPTIONS = [
  { value: 'RS', label: 'Societies' },
  { value: 'CP', label: 'Corporate Parks' },
  { value: 'BS', label: 'Bus Shelter' },
  { value: 'GY', label: 'Gym' },
  { value: 'SA', label: 'Saloon' },
  { value: 'RE', label: 'Retail Shop' },
];

function emptyCenter(name = '') {
  return {
    center: {
      center_name: name,
      address: '',
      latitude: '',
      longitude: '',
      radius: 0,
      subarea: '',
      area: '',
      city: '',
      pincode: '',
      codes: ['RS'],
    },
  };
}

function getPrimaryCenter(row) {
  return row.centers?.[0] ?? emptyCenter(row.name);
}

function selectedGoals(setup) {
  return setup.campaigns.map((c) => c.goal);
}

export default function Goal() {
  const { draft, setDraft, steps, stepErrors, currentStep } = useWizard();
  const setup = draft.campaign_setup;
  const { loading, error, missingGoals, buildCampaignRows } = useCampaignTemplates();

  const businessTypeId = draft.organisation.business_type[0] ?? '';

  const updateCampaign = useCallback(
    (goal, updater) => {
      setDraft((prev) => ({
        ...prev,
        campaign_setup: {
          ...prev.campaign_setup,
          campaigns: prev.campaign_setup.campaigns.map((row) =>
            row.goal === goal ? updater(row) : row,
          ),
        },
      }));
    },
    [setDraft],
  );

  const applyGoals = useCallback(
    async (goals) => {
      const rows = await buildCampaignRows(
        businessTypeId,
        goals,
        setup.campaigns,
      );
      const primary =
        setup.primary_goal && goals.includes(setup.primary_goal)
          ? setup.primary_goal
          : goals[0] ?? '';
      setDraft((prev) => ({
        ...prev,
        campaign_setup: {
          primary_goal: primary,
          campaigns: rows,
        },
      }));
    },
    [businessTypeId, buildCampaignRows, setDraft, setup.campaigns, setup.primary_goal],
  );

  const updateCenterField = useCallback(
    (goal, field, value) => {
      updateCampaign(goal, (row) => {
        const current = getPrimaryCenter(row);
        return {
          ...row,
          centers: [
            {
              center: {
                ...current.center,
                [field]: value,
              },
            },
          ],
        };
      });
    },
    [updateCampaign],
  );

  const onToggleGoal = (goal, checked) => {
    const current = selectedGoals(setup);
    const next = checked ? [...current, goal] : current.filter((g) => g !== goal);
    void applyGoals(next);
  };

  const goalError = useMemo(() => {
    return (
      stepErrors.goals ||
      stepErrors.goal ||
      Object.entries(stepErrors).find(([k]) =>
        k.startsWith('name_') ||
        k.startsWith('tentative_cost_') ||
        k.startsWith('cost_of_lead_') ||
        k.startsWith('center_city_') ||
        k.startsWith('center_area_') ||
        k.startsWith('center_subarea_'),
      )?.[1]
    );
  }, [stepErrors]);

  const fieldError = useCallback(
    (goal, key) => stepErrors[`${key}_${goal}`],
    [stepErrors],
  );

  return (
    <>
      <StepPanel stepId={6} stepStatus={steps.campaign}>
        {goalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {goalError}
          </Alert>
        )}
        {!businessTypeId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select a business type in Organisation Details to load sample campaigns.
          </Alert>
        )}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {missingGoals.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No template for: {missingGoals.map((g) => GOAL_LABEL_BY_VALUE[g] ?? g).join(', ')}. Using
            defaults.
          </Alert>
        )}
        <Typography variant="subtitle2" gutterBottom>
          Campaign objectives (select one or more)
        </Typography>
        <FormGroup>
          {WIZARD_CAMPAIGN_GOALS.map((g) => (
            <FormControlLabel
              key={g.value}
              control={
                <Checkbox
                  checked={selectedGoals(setup).includes(g.value)}
                  onChange={(e) => onToggleGoal(g.value, e.target.checked)}
                  disabled={!businessTypeId || loading}
                />
              }
              label={g.label}
            />
          ))}
        </FormGroup>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading samples…</Typography>
          </Box>
        )}
        {setup.campaigns.length > 1 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Primary objective
            </Typography>
            <RadioGroup
              value={setup.primary_goal}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  campaign_setup: {
                    ...prev.campaign_setup,
                    primary_goal: e.target.value,
                  },
                }))
              }
            >
              {setup.campaigns.map((c) => (
                <FormControlLabel
                  key={c.goal}
                  value={c.goal}
                  control={<Radio />}
                  label={GOAL_LABEL_BY_VALUE[c.goal] ?? c.goal}
                />
              ))}
            </RadioGroup>
          </Box>
        )}
        {setup.campaigns.map((c) => (
          <Box key={c.goal} sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {GOAL_LABEL_BY_VALUE[c.goal] ?? c.goal}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  label={`Campaign name — ${GOAL_LABEL_BY_VALUE[c.goal] ?? c.goal}`}
                  required
                  value={c.name}
                  onChange={(e) =>
                    updateCampaign(c.goal, (row) => {
                      const nextName = e.target.value;
                      const next = { ...row, name: nextName };
                      const current = getPrimaryCenter(row);
                      if (!row.centers?.length || !current.center.center_name || current.center.center_name === row.name) {
                        next.centers = [
                          {
                            center: {
                              ...current.center,
                              center_name: nextName,
                            },
                          },
                        ];
                      }
                      return next;
                    })
                  }
                  error={!!stepErrors[`name_${c.goal}`]}
                  helperText={stepErrors[`name_${c.goal}`]}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  label="Tentative Budget"
                  type="number"
                  value={c.tentative_cost ?? ''}
                  onChange={(e) => updateCampaign(c.goal, (row) => ({ ...row, tentative_cost: Number(e.target.value || 0) }))}
                  error={!!fieldError(c.goal, 'tentative_cost')}
                  helperText={fieldError(c.goal, 'tentative_cost')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  label="Cost of Leads"
                  type="number"
                  value={c.cost_of_lead ?? ''}
                  onChange={(e) => updateCampaign(c.goal, (row) => ({ ...row, cost_of_lead: Number(e.target.value || 0) }))}
                  error={!!fieldError(c.goal, 'cost_of_lead')}
                  helperText={fieldError(c.goal, 'cost_of_lead')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  select
                  label="Brand"
                  value={c.brand ?? 'single_brand'}
                  onChange={(e) => updateCampaign(c.goal, (row) => ({ ...row, brand: e.target.value }))}
                >
                  {BRAND_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Description"
                  multiline
                  minRows={3}
                  value={c.description ?? ''}
                  onChange={(e) => updateCampaign(c.goal, (row) => ({ ...row, description: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Campaign location details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  label="Principal Vendor"
                  value={draft.organisation.organisation_id || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="Account"
                  value={draft.account.account_id || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  label="City"
                  value={getPrimaryCenter(c).center.city}
                  onChange={(e) => updateCenterField(c.goal, 'city', e.target.value)}
                  error={!!fieldError(c.goal, 'center_city')}
                  helperText={fieldError(c.goal, 'center_city')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  label="Area"
                  value={getPrimaryCenter(c).center.area}
                  onChange={(e) => updateCenterField(c.goal, 'area', e.target.value)}
                  error={!!fieldError(c.goal, 'center_area')}
                  helperText={fieldError(c.goal, 'center_area')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  label="Sub Area"
                  value={getPrimaryCenter(c).center.subarea}
                  onChange={(e) => updateCenterField(c.goal, 'subarea', e.target.value)}
                  error={!!fieldError(c.goal, 'center_subarea')}
                  helperText={fieldError(c.goal, 'center_subarea')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  label="Location Address"
                  value={getPrimaryCenter(c).center.address}
                  onChange={(e) => updateCenterField(c.goal, 'address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Pincode"
                  value={getPrimaryCenter(c).center.pincode}
                  onChange={(e) => updateCenterField(c.goal, 'pincode', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Radius (Km)"
                  type="number"
                  value={getPrimaryCenter(c).center.radius}
                  onChange={(e) => updateCenterField(c.goal, 'radius', Number(e.target.value || 0))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  label="Center Name"
                  value={getPrimaryCenter(c).center.center_name}
                  onChange={(e) => updateCenterField(c.goal, 'center_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Space types
                </Typography>
                <FormGroup row>
                  {SPACE_CODE_OPTIONS.map((option) => {
                    const codes = getPrimaryCenter(c).center.codes || [];
                    return (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={codes.includes(option.value)}
                            onChange={(e) => {
                              const nextCodes = e.target.checked
                                ? Array.from(new Set([...codes, option.value]))
                                : codes.filter((code) => code !== option.value);
                              updateCenterField(c.goal, 'codes', nextCodes);
                            }}
                          />
                        }
                        label={option.label}
                      />
                    );
                  })}
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        ))}
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
